import { Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { ReactNode, useCallback } from 'react';
// Colors.neutral_divider (#EDEDED) has no equivalent in the DS color scale — kept until DS adds it
import { Colors } from '~/themes/colors';
import { Column } from '../Column';
import { Text } from '../Text';
import { IBoxProps } from '../Box';

type TContext = {
  onPlayAnimation(): void;
};

interface IPulseCardProps {
  ActionComponent: (ctx: TContext) => ReactNode;
  ComponentProps?: Partial<{ BoxProps: IBoxProps; DescriptionProps: React.CSSProperties }>;
  description: string | ReactNode;
  isItalic?: boolean;
  title: string;
}

/**
 * A card component that displays a title and description in a styled container.
 *
 * This component renders a centered card with a border, containing a bold title
 * and an italicized description. It's designed to be used for displaying
 * informational content or status messages.
 *
 * @remarks
 * Pulse animation functionality is planned for future implementation.
 *
 * @example
 * ```tsx
 * <PulseCard
 *   title="Welcome"
 *   description="This is a sample description"
 * />
 * ```
 *
 * @param props - The component props
 * @returns A styled card component with title and description
 */
export const PulseCard = ({
  ActionComponent,
  ComponentProps,
  description,
  isItalic = true,
  title,
}: IPulseCardProps) => {
  // TODO: play Pulse animation logic here
  const onPlayAnimation = useCallback(() => {}, []);

  return (
    <Column
      borderWidth={1.5}
      borderStyle="solid"
      borderColor={Colors.neutral_divider}
      borderRadius={8}
      width="100%"
      justifyContent="center"
      alignItems="center"
      paddingBlock={22}
      paddingInline={18}
      paddingBottom={38}
      {...ComponentProps?.BoxProps}
    >
      <Text bold fontSize={18}>
        {title}
      </Text>
      <Typography
        italic={isItalic}
        color={colors.neutral[500]}
        style={{ marginBottom: 22, marginTop: 12, textAlign: 'center', ...ComponentProps?.DescriptionProps }}
      >
        {description}
      </Typography>
      {ActionComponent({ onPlayAnimation })}
    </Column>
  );
};
